import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Box, Flex } from 'grid-styled';
import { FormattedHTMLMessage, FormattedMessage, injectIntl } from 'react-intl';
import Media from 'react-media';
import styles from 'theme';
import H2 from 'components/H2/H2';
import messages from './messages';

import { ROUTE_ANALYSE_OVERVIEW } from 'RouteList';
import VideoAnalyseView from './components/VideoAnalyseView';
import AnalyseCaroussel, { Thumb } from './components/AnalyseCaroussel';
import AnalyseDropZone from './components/AnalyseDropZone';
import { Query } from 'react-apollo';
import { QUERY_GET_VIDEO_ANALYSES } from '../../services/graphql/video-analyse-query';
import P from '../../components/P/P';
import { VideoAnalyse } from './store/model/index';
import { sort, SORT_DATA_TYPES } from '../../utils/array';

@inject('authStore', 'routing', 'uiState', 'fileUploadStore')
@observer
class Overview extends Component {
  state = {
    email: ''
  };

  componentDidMount() {
    window.scrollTo(0, 0);
  }

  onDrop = acceptedFiles => {
    const { fileUploadStore } = this.props;
    if (acceptedFiles.length > 0) {
      fileUploadStore.uploadVideos(acceptedFiles);
    }
  };

  setVideoAnalyseActive = videoAnalyse => {
    const { uiState, routing: { push } } = this.props;
    if (videoAnalyse.video.transcoded) {
      push(`${ROUTE_ANALYSE_OVERVIEW}/${videoAnalyse.id}`);
    } else {
      uiState.createAlert({
        title: this.props.intl.formatMessage(
          messages.analyseOverviewModalProgressTitle
        ),
        message: this.props.intl.formatMessage(
          messages.analyseOverviewModalProgressDescription
        ),
        cancel: this.props.intl.formatMessage(
          messages.analyseOverviewModalProgressButton
        )
      });
    }
  };

  shareVideo = videoAnalyse => {
    this.setVideoAnalyseActive(videoAnalyse);
    setTimeout(() => {
      const element = document.getElementById('shareToEmailInput');
      if (element) {
        element.focus();
      }
    }, 100);
  };

  deleteAnalyse = async (mutation, analyse) => {
    const { uiState, intl } = this.props;
    uiState.createAlert({
      title: intl.formatMessage(
        messages.analyseOverviewModalDeleteAnalyseTitle
      ),
      message: intl.formatMessage(
        messages.analyseOverviewModalDeleteAnalyseDescription
      ),
      ok: intl.formatMessage(
        messages.analyseOverviewModalDeleteAnalyseButtonOK
      ),
      okHandler: async () => {
        await mutation({ variables: { analyseId: analyse.id } });
      },
      cancel: intl.formatMessage(
        messages.analyseOverviewModalDeleteAnalyseButtonCancel
      )
    });
  };

  deleteVideoAnalyse = async (mutation, videoAnalyse) => {
    const { uiState, intl, routing: { push } } = this.props;
    uiState.createAlert({
      title: intl.formatMessage(messages.analyseOverviewModalDeleteVideoTitle),
      message: intl.formatMessage(
        messages.analyseOverviewModalDeleteVideoDescription
      ),
      ok: intl.formatMessage(messages.analyseOverviewModalDeleteVideoButtonOK),
      okHandler: async () => {
        await mutation({ variables: { videoAnalyseId: videoAnalyse.id } });
        push(ROUTE_ANALYSE_OVERVIEW);
      },
      cancel: intl.formatMessage(
        messages.analyseOverviewModalDeleteVideoButtonCancel
      )
    });
  };

  onUpdateEmail = e => {
    this.setState({ email: e.target.value });
  };

  render() {
    const {
      fileUploadStore: { canDrop },
      authStore: { isImpersonator, user },
      videoAnalyseId
    } = this.props;

    const { email } = this.state;
    const currentUser = isImpersonator || user;

    return (
      <Query
        query={QUERY_GET_VIDEO_ANALYSES}
        variables={{ personId: isImpersonator ? isImpersonator.id : user.id }}
        fetchPolicy={'cache-and-network'}
      >
        {({ loading, error, data }) => {
          if (loading) return <P>Bezig met laden...</P>;
          if (error) return <P danger>{error.message}</P>;
          let items =
            (data &&
              data.getVideoAnalyses &&
              data.getVideoAnalyses.map(e => new VideoAnalyse(e))) ||
            [];

          const activeVideoAnalyse = videoAnalyseId
            ? items.find(i => i.id === videoAnalyseId)
            : null;

          items = sort(items, {
            keys: [
              {
                key: 'date_created',
                dataType: SORT_DATA_TYPES.DATE_STRING,
                desc: true
              }
            ]
          });

          const hasVideoAnalyses = items.length > 0;
          return (
            <Flex flexWrap="wrap" width={1 / 1}>
              <Box width={1 / 1} mt={2} mb={4}>
                <H2 upper>
                  <FormattedMessage {...messages.analiseOverviewTitle} />
                </H2>
              </Box>
              <FormattedHTMLMessage {...messages.analiseIntroText} />
              <Flex width={1 / 1} flexWrap="wrap">
                {!isImpersonator && (
                  <Box width={[1, 1 / 3, 1 / 4]} pr={[0, 2, 2]} pb={[3, 0]}>
                    <AnalyseDropZone onDrop={this.onDrop} canDrop={canDrop} />
                  </Box>
                )}
                <Flex
                  width={[1, 2 / 3, isImpersonator ? 1 / 1 : 3 / 4]}
                  pl={[0, 2, 2]}
                  alignItems={!hasVideoAnalyses ? 'center' : 'stretch'}
                >
                  {hasVideoAnalyses &&
                    items.length === 1 &&
                    items.map(
                      (data, index) =>
                        data &&
                        data.video && (
                          <Box
                            key={data.id}
                            width={1 / 3}
                            style={{ position: 'relative' }}
                          >
                            <Thumb
                              isactive={
                                activeVideoAnalyse
                                  ? data.id === activeVideoAnalyse.id
                                  : false
                              }
                              videoAnalyse={data}
                              onShare={this.shareVideo}
                              sharedBy={data.sharedFrom}
                              sharedToMe={
                                data.sharedFrom &&
                                data.sharedFrom.id !== currentUser.id
                              }
                              shareable={
                                data.sharedFrom &&
                                data.sharedFrom.id === currentUser.id
                              }
                              video={data.video}
                              onClick={() => this.setVideoAnalyseActive(data)}
                            />
                          </Box>
                        )
                    )}
                  {hasVideoAnalyses &&
                    items.length > 1 && (
                      <Media query={{ maxWidth: styles.mq.large }}>
                        {matches => (
                          <AnalyseCaroussel
                            items={items}
                            activeId={
                              activeVideoAnalyse && activeVideoAnalyse.id
                            }
                            user={currentUser}
                            onClick={this.setVideoAnalyseActive}
                            onShare={this.shareVideo}
                            slidesToShow={
                              matches
                                ? isImpersonator ? 3 : 2
                                : isImpersonator ? 4 : 3
                            }
                          />
                        )}
                      </Media>
                    )}

                  {!hasVideoAnalyses && (
                    // <Flex alignItems="center">
                    <FormattedMessage {...messages.analiseNoAnalyses} />
                    // </Flex>
                  )}
                </Flex>
              </Flex>
              {activeVideoAnalyse && (
                <VideoAnalyseView
                  videoAnalyse={activeVideoAnalyse}
                  deleteVideoAnalyse={this.deleteVideoAnalyse}
                  deleteAnalyse={this.deleteAnalyse}
                  showControls={!isImpersonator}
                  onUpdateEmail={this.onUpdateEmail}
                  email={email}
                />
              )}
            </Flex>
          );
        }}
      </Query>
    );
  }
}
export default injectIntl(Overview);



// WEBPACK FOOTER //
// ./src/containers/analyse/Overview.js